import {useState} from 'react';
import PropTypes from 'prop-types';
import {useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import roundTo from 'lodash/round';

import Box from "@mui/material/Box";
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from "@mui/material/Typography";
import NewReleasesIcon from '@mui/icons-material/NewReleases';

import {
  getSessionCategory,
  formatSessionDate,
  formatSessionTime,
  formatSessionLocation,
  formatSessionVirtualLink, 
  getSessionLink,
  formatSessionTitle
} from '../../helpers/sessions';
import {formatPatientName, formatPatientPhone} from '../../helpers/patients';
import {formatProviderName} from '../../helpers/providers';
import {formatCaseName} from '../../helpers/cases';
// import SessionBookmark from './SessionBookmark';
import BetaFeature from '../common/BetaFeature';
import SessionStatus from './SessionStatus';
import SessionActions from './SessionActions';
import EditSessionCommentDialog from './EditSessionCommentDialog';
import config from "../../config/config";

const getColumns = ({
  showAllProviders, 
  isPatient, 
  navigate,
  preventParentEvent,
  afterSave,
  setIsOpen,
}) => [
  {
    key: 'status',
    renderCellContent: s => <SessionStatus session={s} />,
  },
  {
    key: 'actions',
    minWidth: 110,
    renderCellContent: s => (
      <Stack direction='row' spacing={2} onClick={preventParentEvent}>
          <SessionActions
            session={s}
            isPatient={isPatient}
            variant='icon'
            onSave={afterSave}
          />
      </Stack>
    ),
  },
  {
    key: 'provider',
    renderCellContent: s => formatProviderName(s.provider),
    hidden: s => !showAllProviders,
  },
  {
    key: 'date',
    renderCellContent: s => (
      <Typography variant='small500Inter'>
        {formatSessionDate(s, 'M/D/YYYY')}, {formatSessionTime(s)}
      </Typography>
    ),
  },
  {
    key: 'patient',
    minWidth: 150,
    renderCellContent: s => (
      isPatient
      ? formatProviderName(s.provider)
      : <Button  
        sx={{color: config().colors.text, textAlign: 'start'}} 
        onClick={(event) => {
          preventParentEvent(event);
          navigate(`/providers/patients/view/${s.patient.id}`);
      }}>
          <Stack direction='row' alignItems='center' gap={.25}>
            {s.is_discovery ? <NewReleasesIcon 
              style={{ color: config().colors.success, fontSize: '1.2rem' }} 
            /> : null}
            <Typography variant='small500Inter'>
              {formatPatientName(s.patient)}
            </Typography>
          </Stack>
      </Button>
    ),
  },
  {
    key: 'case',
    minWidth: 125,
    renderCellContent: s => (
      <Typography variant='small500Inter'>
        {formatCaseName(s.case)}
      </Typography>
    ),
    hidden: s => isPatient,
  },
  {
    key: 'category',
    renderCellContent: s => getSessionCategory(s),
    hidden: s => isPatient,
  },
  {
    key: 'patient.phone',
    minWidth: 100,
    renderCellContent: s => (
      isPatient
      ? null
      :
        <Box onClick={preventParentEvent}>
          {formatPatientPhone(s.patient, {returnLinkText: true})}
        </Box>
    ),
    hidden: s => isPatient,
  },
  {
    key: 'total',
    width: 75,
    renderCellContent: s => (
      !s.is_completed
      ? null
      : <Stack direction='row' justifyContent='center'>
        {`$${roundTo(s.total, 2).toFixed(2)}`}
      </Stack>
    ),
  },
  {
    key: 'session_type',
    minWidth: 150,
    renderCellContent: s => (
      <Typography variant='small500Inter'>
        {formatSessionTitle(s)}
      </Typography>
    ),
    hidden: s => isPatient,
  },
  {
    key: 'location',
    minWidth: 180,
    renderCellContent: s => (
      <Box sx={{width: 'fit-content'}} onClick={preventParentEvent}>
        {
          (s?.appointment_scheduled?.location_type
            || s?.appointment_scheduled?.session_type?.location_type
          ) === 'VIRTUAL'
          ? formatSessionVirtualLink(s, {returnLinkText: true})
          : formatSessionLocation(s, {returnLinkText: true})
        }
      </Box>
    ),
  },
  {
    key: 'comment',
    hidden: s => isPatient,
    minWidth: 150,
    renderCellContent: s => (
      isPatient ? null :
      <Stack
          direction='row'
          alignItems='center'
          onClick={preventParentEvent}
      >
        <Tooltip title='Edit Memo' onClick={preventParentEvent}>
          <IconButton color='primary' onClick={e => {
            preventParentEvent(e);
            setIsOpen('comment');
          }}>
            <EditIcon 
              style={{fontSize: '0.9rem'}} 
              fill={'var(--primary)'}
              onClick={e => {
                preventParentEvent(e);
                setIsOpen('comment');
              }}
            />
          </IconButton>
        </Tooltip>
        <Typography 
          variant='small500' 
          style={{
            textOverflow: 'ellipsis', 
            overflow: 'hidden', 
            whiteSpace: 'nowrap',
            maxWidth: '500px',
            minWidth: '150px'
          }}
        >
          {s.comment || '-'}
        </Typography>
      </Stack>
    ),
  },
];

const SessionTableRow = props => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const {data: s, isPatient, afterSave, showAllProviders} = props;
  const url = getSessionLink(s.id);
  const viewSessionDetail = () => navigate(url);
  const preventParentEvent = (event) => {
    event.stopPropagation();
  };
  const selectedColumns = useSelector(state => state.sessionTable.columns);
  const columns = getColumns({
    showAllProviders, 
    isPatient, 
    navigate, 
    preventParentEvent,
    afterSave,
    setIsOpen,
  })
    .filter(col => selectedColumns ? selectedColumns.includes(col.key) : true);
  return (
    <>
      {
        isOpen === 'comment'
          ? <EditSessionCommentDialog
              session={s}
              isOpen={!!isOpen}
              onClose={() => setIsOpen(false)}
          />
          : null
      }
      <TableRow
        key={s.id}
        sx={{
          cursor: 'pointer',
          '&:hover': {background: 'var(--hover-fill)'}
      }}
        onClick={() => viewSessionDetail()}
      >
        {columns
          .map((col, i) => (
            col.hidden && col.hidden(s) ? null :
            (col.isBeta ?
              <BetaFeature hideTag={true}>
                <TableCell 
                  key={i} 
                  align={col.align || 'left'} 
                  style={{minWidth: col.minWidth || 0, width: col.width || null}}
                >
                  {col.renderCellContent(s)}
                </TableCell>
              </BetaFeature>
              : <TableCell 
                key={i} 
                align={col.align || 'left'} 
                style={{minWidth: col.minWidth || 0, width: col.width || null}}
              >
                {col.renderCellContent(s)}
              </TableCell>
            )
          ))
        }
      </TableRow>
    </>
  );
};

SessionTableRow.propTypes = {
  data: PropTypes.object,
  buttons: PropTypes.arrayOf(PropTypes.element),
  isPatient: PropTypes.bool,
  afterSave: PropTypes.func,
  showAllProviders: PropTypes.bool,
};

export default SessionTableRow;
